// src/components/recipient/RecipientBouquet.tsx
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Gift, ChevronRight } from "lucide-react";
import { MoneyBouquet3D } from "../bouquet/MoneyBouquet3D";
import { cn } from "../../utils/helpers";
import { BouquetConfig } from "../../types/recipient.types";

// Extend the BouquetConfig interface locally to include the color props
interface ExtendedBouquetConfig extends BouquetConfig {
  primaryColor?: string;
  secondaryColor?: string;
  theme?: any;
}

interface RecipientBouquetProps {
  config: ExtendedBouquetConfig;
  senderName: string;
  recipientName: string;
  onReceive: () => void;
}

export const RecipientBouquet: React.FC<RecipientBouquetProps> = ({
  config,
  senderName,
  recipientName,
  onReceive,
}) => {
  const [phase, setPhase] = useState<
    | "pause"
    | "identity"
    | "awakening"
    | "collecting"
    | "reveal"
    | "transitioning"
  >("pause");
  const [collectedCount, setCollectedCount] = useState(0);
  const [buttonScale, setButtonScale] = useState(1);
  const [buttonShake, setButtonShake] = useState(0);
  const [petalsFlying, setPetalsFlying] = useState(false);
  const [showExplosion, setShowExplosion] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);

  const totalPetals = config.petals || 5;
  const buttonRef = useRef<HTMLButtonElement>(null);
  const petalsRef = useRef<HTMLDivElement>(null);

  // Use theme colors if available, otherwise fallback
  const primary = config.primaryColor || config.theme?.primary || "#E11D48";
  const secondary =
    config.secondaryColor || config.theme?.secondary || "#000000";

  // Ceremonial pacing
  useEffect(() => {
    const timeline = [
      { phase: "pause", duration: 2000, next: "identity" },
      { phase: "identity", duration: 2500, next: "awakening" },
      { phase: "awakening", duration: 3000, next: "collecting" },
    ];

    const currentPhase = timeline.find((t) => t.phase === phase);
    if (
      currentPhase &&
      phase !== "collecting" &&
      phase !== "reveal" &&
      phase !== "transitioning"
    ) {
      const timer = setTimeout(() => {
        setPhase(currentPhase.next as any);
      }, currentPhase.duration);
      return () => clearTimeout(timer);
    }
  }, [phase]);

  // SINGLE BUTTON COLLECT ALL - Trigger all petals collection at once
  const handleCollectAll = useCallback(() => {
    if (phase !== "collecting") return;

    // Subtle haptic feedback
    if (typeof navigator !== "undefined" && navigator.vibrate) {
      navigator.vibrate([50, 30, 50]); // Pattern for excitement
    }

    // Start petals flying animation
    setPetalsFlying(true);

    // Button grows slightly
    setButtonScale(1.2);

    // After a brief moment, simulate all petals being collected
    setTimeout(() => {
      setCollectedCount(totalPetals);

      // Button shake animation
      setButtonShake(1);
      setTimeout(() => setButtonShake(0), 500);

      // Show explosion effect
      setShowExplosion(true);

      // Button returns to normal
      setTimeout(() => setButtonScale(1), 300);

      // After explosion, move to reveal phase
      setTimeout(() => {
        setShowExplosion(false);
        setPhase("transitioning");

        // Slight pause before reveal
        setTimeout(() => {
          setPhase("reveal");
        }, 800);
      }, 800);
    }, 600); // Time for petals to fly
  }, [phase, totalPetals]);

  // Handle reveal completion and transition to next phase (completion)
  const handleAcceptGift = useCallback(() => {
    // Start fade out animation for cinematic ending
    setFadeOut(true);

    // Wait for fade out, then call onReceive which will move to completion
    setTimeout(() => {
      onReceive();
    }, 1500);
  }, [onReceive]);

  // Generate flying petal elements for animation
  const flyingPetals = useMemo(() => {
    if (!petalsFlying) return null;

    return Array.from({ length: totalPetals }).map((_, i) => {
      // Random starting positions around the screen
      const startX = (Math.random() - 0.5) * 200;
      const startY = (Math.random() - 0.5) * 200;
      const delay = i * 0.05; // Staggered delay

      return (
        <motion.div
          key={`flying-${i}`}
          className="absolute z-50"
          initial={{
            x: startX,
            y: startY,
            opacity: 1,
            scale: 0.8 + Math.random() * 0.7,
            rotate: Math.random() * 360,
          }}
          animate={{
            x: 0,
            y: 0,
            opacity: 0,
            scale: 0,
            rotate: Math.random() * 720,
          }}
          transition={{
            duration: 0.6,
            delay,
            ease: [0.22, 1, 0.36, 1],
          }}
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            marginLeft: -20,
            marginTop: -20,
            pointerEvents: "none",
          }}
        >
          <div
            className="w-10 h-14 rounded-[40%_40%_50%_50%] shadow-xl"
            style={{
              background: `linear-gradient(135deg, ${primary}dd 0%, ${primary}88 100%)`,
              border: "1px solid rgba(255,255,255,0.3)",
            }}
          />
        </motion.div>
      );
    });
  }, [petalsFlying, totalPetals, primary]);

  // Explosion particles
  const explosionParticles = useMemo(() => {
    if (!showExplosion) return null;

    return Array.from({ length: 30 }).map((_, i) => {
      const angle = (i / 30) * Math.PI * 2;
      const distance = 50 + Math.random() * 100;
      const x = Math.cos(angle) * distance;
      const y = Math.sin(angle) * distance;

      return (
        <motion.div
          key={`explosion-${i}`}
          className="absolute z-50 rounded-full"
          initial={{ x: 0, y: 0, scale: 0.5, opacity: 1 }}
          animate={{
            x,
            y,
            scale: 0,
            opacity: 0,
          }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          style={{
            width: 4 + Math.random() * 8,
            height: 4 + Math.random() * 8,
            background: i % 2 === 0 ? primary : "#ffffff",
            left: "50%",
            top: "50%",
            marginLeft: -4,
            marginTop: -4,
            filter: "blur(1px)",
            boxShadow: `0 0 15px ${primary}`,
          }}
        />
      );
    });
  }, [showExplosion, primary]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[1000] overflow-hidden flex flex-col items-center justify-center"
      style={{
        background: `radial-gradient(circle at center, ${secondary}22 0%, #000 100%)`,
      }}
    >
      {/* Ambient glow */}
      <div
        className="absolute inset-0 opacity-40"
        style={{
          background: `radial-gradient(circle at 50% 50%, ${primary}33 0%, transparent 70%)`,
        }}
      />

      {/* Floating particles */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full"
            style={{
              width: "2px",
              height: "2px",
              background: primary,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              filter: "blur(1px)",
            }}
            animate={{
              y: [0, -50, 0],
              opacity: [0, 0.5, 0],
            }}
            transition={{
              duration: 4 + Math.random() * 4,
              repeat: Infinity,
              delay: Math.random() * 4,
            }}
          />
        ))}
      </div>

      {/* Flying petals animation */}
      <AnimatePresence>
        {petalsFlying && (
          <div ref={petalsRef} className="absolute inset-0 pointer-events-none">
            {flyingPetals}
          </div>
        )}
      </AnimatePresence>

      {/* Explosion particles */}
      <AnimatePresence>
        {showExplosion && (
          <div className="absolute inset-0 pointer-events-none">
            {explosionParticles}
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {/* PHASE 1: Sacred pause */}
        {phase === "pause" && (
          <motion.div
            key="pause"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-center z-10"
          >
            <motion.p
              animate={{ opacity: [0.3, 0.6, 0.3] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="text-white/40 text-sm tracking-[.5em] uppercase"
            >
              Someone left you something more
            </motion.p>
          </motion.div>
        )}

        {/* PHASE 2: Identity reveal */}
        {phase === "identity" && (
          <motion.div
            key="identity"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="text-center z-10"
          >
            <span className="text-white/30 block mb-4 uppercase tracking-widest text-[10px]">
              A Gift Prepared By
            </span>
            <motion.h2
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.3, type: "spring" }}
              className="text-white text-5xl md:text-7xl font-serif italic font-light"
              style={{ color: primary }}
            >
              {senderName}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              transition={{ delay: 0.8 }}
              className="text-white/30 text-xs mt-6 tracking-wider"
            >
              For {recipientName}
            </motion.p>
          </motion.div>
        )}

        {/* PHASE 3: Awakening - bouquet appears */}
        {(phase === "awakening" || phase === "collecting") && (
          <motion.div
            key="bouquet"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="w-full h-full flex flex-col items-center justify-center relative px-6 z-10"
          >
            <div className="w-full h-[50vh] max-w-4xl">
              <MoneyBouquet3D
                amount={config.totalValue}
                petals={totalPetals}
                onPetalClick={handleCollectAll} // Now all petals are collected via button
                primaryColor={primary}
                secondaryColor={secondary}
                isInteractive={false} // Disable individual petal clicking
                collectedCount={collectedCount}
              />
            </div>

            <div className="mt-12 text-center">
              <motion.p
                animate={{ opacity: [0.3, 0.6, 0.3] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="text-white/40 text-[10px] tracking-[.3em] uppercase mb-6"
              >
                {phase === "collecting"
                  ? `A gift of KES ${config.totalValue.toLocaleString()} awaits`
                  : "Your gift is appearing..."}
              </motion.p>

              {/* SINGLE COLLECT BUTTON */}
              <AnimatePresence>
                {phase === "collecting" && collectedCount === 0 && (
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.8, opacity: 0 }}
                    className="flex justify-center"
                  >
                    <motion.button
                      ref={buttonRef}
                      onClick={handleCollectAll}
                      animate={{
                        scale: buttonScale,
                        x: buttonShake ? [0, -10, 10, -5, 5, 0] : 0,
                      }}
                      transition={{
                        scale: { type: "spring", damping: 15 },
                        x: { duration: 0.5 },
                      }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="relative px-12 py-6 rounded-full bg-white text-black font-bold text-xl shadow-2xl flex items-center gap-4 overflow-hidden"
                      style={{
                        boxShadow: `0 20px 40px -10px ${primary}cc`,
                      }}
                    >
                      {/* Ripple effect on button */}
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                        animate={{
                          x: ["-100%", "100%"],
                        }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                      />

                      <Gift size={24} style={{ color: primary }} />
                      <span>Collect Your Gift</span>
                      <Sparkles size={20} className="text-primary" />
                    </motion.button>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Progress indicator - becomes visible during collection */}
              <AnimatePresence>
                {collectedCount > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-8"
                  >
                    <div className="flex gap-2 justify-center">
                      {Array.from({ length: totalPetals }).map((_, i) => (
                        <motion.div
                          key={i}
                          initial={{ scale: 0.8 }}
                          animate={{
                            scale: i < collectedCount ? [1, 1.2, 1] : 0.8,
                            backgroundColor:
                              i < collectedCount ? primary : "#ffffff20",
                          }}
                          transition={{ duration: 0.3, delay: i * 0.1 }}
                          className={cn(
                            "h-2 w-8 rounded-full transition-all duration-500",
                            i < collectedCount ? "shadow-lg" : "bg-white/10",
                          )}
                          style={
                            i < collectedCount
                              ? {
                                  background: `linear-gradient(90deg, ${primary}, ${primary}cc)`,
                                  boxShadow: `0 0 15px ${primary}`,
                                }
                              : {}
                          }
                        />
                      ))}
                    </div>
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 0.6 }}
                      className="text-white/40 text-xs mt-4"
                    >
                      {collectedCount === totalPetals
                        ? " Preparing your surprise..."
                        : "Collecting..."}
                    </motion.p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}

        {/* PHASE 4: Reveal - show the value */}
        {phase === "reveal" && (
          <motion.div
            key="reveal"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.1 }}
            className="text-center space-y-8 px-8 max-w-md z-10"
          >
            {/* Ceremonial reveal animation */}
            <motion.div
              animate={{
                scale: [1, 1.3, 1],
                rotate: [0, 10, -10, 0],
              }}
              transition={{ duration: 1.5 }}
            >
              <div className="relative">
                <Gift
                  size={80}
                  className="mx-auto"
                  style={{ color: primary }}
                />
                <motion.div
                  className="absolute inset-0"
                  animate={{
                    boxShadow: [
                      `0 0 0px ${primary}`,
                      `0 0 40px ${primary}`,
                      `0 0 0px ${primary}`,
                    ],
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
              </div>
            </motion.div>

            <motion.h3
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-6xl md:text-8xl font-black text-white tracking-tighter"
            >
              KES {config.totalValue.toLocaleString()}
            </motion.h3>

            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-white/60 font-serif italic text-lg leading-relaxed"
            >
              {config.message}
            </motion.p>

            <motion.button
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7 }}
              onClick={handleAcceptGift}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className="w-full py-6 rounded-full bg-white text-black font-bold flex items-center justify-center gap-3 hover:bg-primary hover:text-white transition-all shadow-2xl text-lg"
              style={{
                boxShadow: `0 20px 30px -10px ${primary}80`,
              }}
            >
              Accept This Gift <ChevronRight size={24} />
            </motion.button>
          </motion.div>
        )}

        {/* Transition phase - cinematic fade */}
        {phase === "transitioning" && (
          <motion.div
            key="transition"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center z-10"
          >
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.3, 0.8, 0.3],
              }}
              transition={{ duration: 1.5 }}
            >
              <Sparkles
                size={60}
                className="mx-auto"
                style={{ color: primary }}
              />
            </motion.div>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-white/60 text-sm mt-4"
            >
              Your gift is ready...
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cinematic fade out overlay for ending */}
      <AnimatePresence>
        {fadeOut && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5 }}
            className="absolute inset-0 bg-black z-50 pointer-events-none"
          />
        )}
      </AnimatePresence>

      {/* Guardian presence */}
      <div className="absolute bottom-8 opacity-20 text-[8px] tracking-[0.5em] text-white/30">
        LRNSY • Guardian of Moments
      </div>
    </motion.div>
  );
};
